import type { AgrianTypes } from "../types/AgrianTypes";
import type { AgrianEventChoice, AgrianEventSelections } from "../types/AgrianEventChoice";
import { isEventAssociatedWithField } from "./isEventAssociatedWithField";

/** Options for the default-event heuristic. An options object rather than a positional flag —
 *  the functions already take four event-list parameters, and a fifth bare boolean at the call
 *  sites would be unreadable. */
export interface PickEventOptions {
	/** Consider sample events only: no target-event or zone-map fallback. */
	preExistingEventsOnly?: boolean;
}

/**
 * Picks the default event-source for a single field given the prefetched
 * event lists. The recency heuristic is:
 *
 *  - Prefer the most-recent **sample** event for this field.
 *  - Else the most-recent **target** event for this field.
 *  - Else the most-recent **zone-map** for this field.
 *  - Else `null` (boundary-only — the field has no events worth importing).
 *
 * "Most recent" sorts by `created_at` descending. Events without an `id`
 * are filtered out before sorting (an Agrian quirk: hydration sometimes
 * yields entries without an id, which can't be selected).
 *
 * Returns the chosen `AgrianEventChoice` (or `null`) and the total count
 * of viable candidates. The caller folds `candidateCount > 1` into the
 * "ambiguity" flag the UI uses to prompt the user.
 *
 * Pure: no IO; the only Date use is `new Date(<created_at>).getTime()`
 * which is deterministic given fixed input strings.
 */
export function pickDefaultEventForField(
	field: AgrianTypes.Field,
	sampleEvents: readonly AgrianTypes.HydratedSampleEvent[],
	targetEvents: readonly AgrianTypes.HydratedTargetEvent[],
	zones: readonly AgrianTypes.HydratedZone[],
	options: PickEventOptions = {},
): { selection: AgrianEventChoice | null; candidateCount: number } {
	// Pre-existing companies source only from sample events, so targets and zone maps are
	// dropped before anything else — including `candidateCount`, which means a field with one
	// sample event and two target events is no longer "ambiguous". That is intended: the
	// targets are not choosable, so there is nothing to disambiguate.
	const preExistingOnly = options.preExistingEventsOnly ?? false;

	const fieldSamples = sampleEvents
		.filter((e) => e.field_id === field.id && !!e.id)
		.slice()
		.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
	const fieldTargets = preExistingOnly
		? []
		: targetEvents
				.filter((e) => !!e.id && isEventAssociatedWithField(e, field))
				.slice()
				.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
	const fieldZones = preExistingOnly
		? []
		: zones
				.filter((e) => e.field_id === field.id && !!e.id)
				.slice()
				.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

	const candidateCount = fieldSamples.length + fieldTargets.length + fieldZones.length;

	if (fieldSamples[0]?.id) return { selection: { source: "sample", eventId: fieldSamples[0].id }, candidateCount };
	if (fieldTargets[0]?.id) return { selection: { source: "target", eventId: fieldTargets[0].id }, candidateCount };
	if (fieldZones[0]?.id) return { selection: { source: "zone-map", eventId: fieldZones[0].id }, candidateCount };
	return { selection: null, candidateCount };
}

/**
 * Batch-version of `pickDefaultEventForField`: runs the per-field rule
 * for every field and rolls up `hasAmbiguity` (true iff any field has
 * more than one candidate event across the sources still in play).
 *
 * Also reports `fieldsWithoutEvents` — the ids of fields that ended with no selection at all.
 * Populated regardless of `preExistingEventsOnly`, so the caller can surface it either way; in
 * the default case it is usually empty, because a field with only a zone map still gets one.
 * Under `preExistingEventsOnly` these are exactly the fields that will not produce a job.
 */
export function pickDefaultEventsForFields(
	fields: readonly AgrianTypes.Field[],
	sampleEvents: readonly AgrianTypes.HydratedSampleEvent[],
	targetEvents: readonly AgrianTypes.HydratedTargetEvent[],
	zones: readonly AgrianTypes.HydratedZone[],
	options: PickEventOptions = {},
): { selections: AgrianEventSelections; hasAmbiguity: boolean; fieldsWithoutEvents: string[] } {
	const selections: AgrianEventSelections = {};
	const fieldsWithoutEvents: string[] = [];
	let hasAmbiguity = false;
	for (const field of fields) {
		const r = pickDefaultEventForField(field, sampleEvents, targetEvents, zones, options);
		if (r.candidateCount > 1) hasAmbiguity = true;
		if (!r.selection) fieldsWithoutEvents.push(field.id);
		selections[field.id] = r.selection;
	}
	return { selections, hasAmbiguity, fieldsWithoutEvents };
}
